import React from 'react';
import StructureColorComponent from '../molstar/Controls/StructureColorComponent';
import StructureRepresentationComponent, {
   Representation,
   StructureComponentType,
} from '../molstar/Controls/StructureRepresentationComponent';

type StructureControlValues = {
   chain: {
      representation: Representation;
      color: string;
   };
   pocket: {
      representation: Representation;
      color: string;
   };
   ligand?: {
      representation: Representation;
      color: string;
   };
};

type StructureControlPairProps = {
   values: StructureControlValues;
   onRepresentationChange: (target: StructureComponentType, type: Representation) => void;
   onColorChange: (target: StructureComponentType, color: string) => void;
   mode: 'small' | 'big';
};

/**
 * Combined representation + color control panel rendered inside the per-row edit
 * panel. Shows dropdowns for chain, pocket, and (optionally) ligand representation
 * types, followed by color swatches for each.
 *
 * Ligand controls are only rendered when `values.ligand` is defined, allowing the
 * panel to be reused for apo structures (no ligand component) and holo structures.
 */
const StructureRepresentationControlComponent = ({
   values,
   onRepresentationChange,
   onColorChange,
   mode,
}: StructureControlPairProps): React.JSX.Element => {
   const chainRepresentationTitle = 'Chain Representation';
   const pocketRepresentationTitle = 'Pocket Representation';
   const ligandRepresentationTitle = 'Ligand Representation';

   return (
      <div className={`structure-control structure-control-${mode}`}>
         <StructureRepresentationComponent
            selectedRepresentation={values.chain.representation}
            changeRepresentation={(type): void => onRepresentationChange('chain', type)}
            title={chainRepresentationTitle}
            showSelectedRepresentation={true}
         />
         <StructureRepresentationComponent
            selectedRepresentation={values.pocket.representation}
            changeRepresentation={(type): void => onRepresentationChange('pocket', type)}
            title={pocketRepresentationTitle}
            showSelectedRepresentation={true}
         />
         {values.ligand && (
            <StructureRepresentationComponent
               selectedRepresentation={values.ligand.representation}
               changeRepresentation={(type): void => onRepresentationChange('ligand', type)}
               title={ligandRepresentationTitle}
               showSelectedRepresentation={true}
            />
         )}
         <StructureColorComponent
            selectedColor={values.chain.color}
            changeColor={(color): void => onColorChange('chain', color)}
            title="Chain color"
         />
         <StructureColorComponent
            selectedColor={values.pocket.color}
            changeColor={(color): void => onColorChange('pocket', color)}
            title="Pocket color"
         />
         {values.ligand && (
            <StructureColorComponent
               selectedColor={values.ligand.color}
               changeColor={(color): void => onColorChange('ligand', color)}
               title="Ligand color"
            />
         )}
      </div>
   );
};

export default StructureRepresentationControlComponent;
